import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "Helper/AuthTokenHelper";
import { toast } from "react-toastify";

let initialState = {
  notificationList: {},
  notificationListLoading: false,
  notificationCurrentPage: 0,
  notificationPageLimit: 0,
  allowNotificationToggle: false,
  notificationPermissionDialogVisible: false,
};

export const getNotificationList = createAsyncThunk(
  "notification/list-notification",
  (payload) => {
    const { location_id, start, limit } = payload;

    return new Promise((resolve, reject) => {
      axios
        .post(`notification/list-notification/${location_id}`, {
          start,
          limit,
        })
        .then(({ data }) => {
          if (data?.err === 0) {
            resolve({ data: data?.data });
          } else {
            toast.error(data?.msg);
            reject(data);
          }
        })
        .catch((errors) => {
          toast.error(errors);
          reject(errors);
        });
    });
  }
);

export const readSingleNotification = createAsyncThunk(
  "notification/read-single-notification",
  (payload) => {
    const { location_id, notification_id } = payload;

    return new Promise((resolve, reject) => {
      axios
        .post(`notification/read-single-notification/${location_id}`, {
          notification_id,
        })
        .then(({ data }) => {
          if (data?.err === 0) {
            toast.success(data?.msg);
            resolve({ data: data?.data });
          } else {
            toast.error(data?.msg);
            reject(data);
          }
        })
        .catch((errors) => {
          toast.error(errors);
          reject(errors);
        });
    });
  }
);

export const readAllNotification = createAsyncThunk(
  "notification/read-notification",
  (payload) => {
    const { location_id } = payload;

    return new Promise((resolve, reject) => {
      axios
        .post(`notification/read-notification/${location_id}`)
        .then(({ data }) => {
          if (data?.err === 0) {
            toast.success(data?.msg);
            resolve({ data: data?.data });
          } else {
            toast.error(data?.msg);
            reject(data);
          }
        })
        .catch((errors) => {
          toast.error(errors);
          reject(errors);
        });
    });
  }
);

export const clearNotification = createAsyncThunk(
  "notification/clear-notification",
  (payload) => {
    const { location_id } = payload;

    return new Promise((resolve, reject) => {
      axios
        .post(`notification/clear-notification/${location_id}`)
        .then(({ data }) => {
          if (data?.err === 0) {
            toast.success(data?.msg);
            resolve({ data: data?.data });
          } else {
            toast.error(data?.msg);
            reject(data);
          }
        })
        .catch((errors) => {
          toast.error(errors);
          reject(errors);
        });
    });
  }
);

export const notificationSubscribe = createAsyncThunk(
  "notification/subscribe",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { location_id } = payload;
    const userPreferences = getAuthToken();

    try {
      // 🔔 Ask for permission
      let permission = Notification.permission;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
        if (permission !== "granted") {
          const { notification } = getState();
          const currentToggle = notification.allowNotificationToggle;

          dispatch(setNotificationPermissionDialogVisible(true));
          dispatch(setAllowNotificationToggle(!currentToggle));
          toast.error("Notification permission denied");
          return rejectWithValue("Notification permission denied");
        }
      }

      const registration = await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey:
          "BMbMsq42qMDe654ubtiL0gTO_c-ieeCIbK1-mijd9jhiV6OgXlyLdMbW9ral7ftuffVqZChcAmPcgHA_c2dDz8Y",
      });

      const { data } = await axios.post(
        `/notification/subscribe/${location_id}`,
        subscription
      );

      if (data?.err === 0) {
        toast.success(data?.msg);

        localStorage.setItem(
          "UserPreferences",
          window.btoa(
            JSON.stringify({
              ...userPreferences,
              notification_enabled: true,
            })
          )
        );

        return data?.data;
      } else {
      }
    } catch (error) {
      toast.error(error?.message || "Subscription failed");
      return rejectWithValue(error?.message || "Subscription failed");
    }
  }
);

export const notificationUnSubscribe = createAsyncThunk(
  "notification/unsubscribe",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { location_id } = payload;
    const userPreferences = getAuthToken();

    try {
      // 🔔 Ask for permission if not already granted
      let permission = Notification.permission;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
        if (permission !== "granted") {
          const { notification } = getState();
          const currentToggle = notification.allowNotificationToggle;

          dispatch(setNotificationPermissionDialogVisible(true));
          dispatch(setAllowNotificationToggle(!currentToggle));
          toast.error("Notification permission denied");
          return rejectWithValue("Notification permission denied");
        }
      }

      const registration = await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey:
          "BMbMsq42qMDe654ubtiL0gTO_c-ieeCIbK1-mijd9jhiV6OgXlyLdMbW9ral7ftuffVqZChcAmPcgHA_c2dDz8Y",
      });

      const { data } = await axios.post(
        `/notification/unsubscribe/${location_id}`,
        subscription
      );

      if (data?.err === 0) {
        toast.success(data?.msg);

        localStorage.setItem(
          "UserPreferences",
          window.btoa(
            JSON.stringify({
              ...userPreferences,
              notification_enabled: false,
            })
          )
        );

        return data?.data;
      } else {
      }
    } catch (error) {
      toast.error(error?.message || "Unsubscription failed");
      return rejectWithValue(error?.message || "Unsubscription failed");
    }
  }
);

export const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    setNotificationList: (state, action) => {
      state.notificationList = action.payload;
    },
    setAllowNotificationToggle: (state, action) => {
      state.allowNotificationToggle = action.payload;
    },
    setNotificationPermissionDialogVisible: (state, action) => {
      state.notificationPermissionDialogVisible = action.payload;
    },
  },
  extraReducers: {
    [getNotificationList.pending]: (state) => {
      state.notificationListLoading = true;
    },
    [getNotificationList.rejected]: (state) => {
      state.notificationList = {};
      state.notificationListLoading = false;
    },
    [getNotificationList.fulfilled]: (state, action) => {
      state.notificationList = action.payload?.data;
      state.notificationListLoading = false;
    },
    [readSingleNotification.pending]: (state) => {
      state.notificationListLoading = true;
    },
    [readSingleNotification.rejected]: (state) => {
      state.notificationListLoading = false;
    },
    [readSingleNotification.fulfilled]: (state, action) => {
      state.notificationListLoading = false;
    },
    [readAllNotification.pending]: (state) => {
      state.notificationListLoading = true;
    },
    [readAllNotification.rejected]: (state) => {
      state.notificationListLoading = false;
    },
    [readAllNotification.fulfilled]: (state, action) => {
      state.notificationListLoading = false;
    },
    [clearNotification.pending]: (state) => {
      state.notificationListLoading = true;
    },
    [clearNotification.rejected]: (state) => {
      state.notificationListLoading = false;
    },
    [clearNotification.fulfilled]: (state, action) => {
      state.notificationList = action.payload?.data;
      state.notificationListLoading = false;
    },
  },
});

export const {
  setNotificationList,
  setAllowNotificationToggle,
  setNotificationPermissionDialogVisible,
} = notificationSlice.actions;

export default notificationSlice.reducer;
